import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import BirthdayCelebration from './BirthdayCelebration';
import { 
  isTodayUserBirthday, 
  calculateAge, 
  getBirthdayMessages,
  showBirthdayNotification,
  markBirthdayCelebrated,
  wasBirthdayCelebratedToday 
} from '../utils/birthdayUtils';

const BirthdayWrapper = ({ children }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const [showBirthdayCelebration, setShowBirthdayCelebration] = useState(false);
  const [birthdayMessages, setBirthdayMessages] = useState([]);

  useEffect(() => {
    const checkBirthday = async () => {
      console.log('🔍 Birthday check started for user:', userInfo?.name);
      console.log('📅 User DOB:', userInfo?.dateOfBirth);
      console.log('📅 Today\'s date:', new Date().toDateString());
      
      if (!userInfo || !userInfo._id) {
        console.log('❌ No user info available');
        return;
      }

      // For testing: Check if URL has birthday test parameter
      const urlParams = new URLSearchParams(window.location.search);
      const testBirthday = urlParams.get('testBirthday') === 'true';
      
      if (testBirthday) {
        console.log('🧪 Birthday test mode activated!');
        const age = calculateAge(userInfo.dateOfBirth) || 25;
        const messages = getBirthdayMessages(userInfo.name, age);
        setBirthdayMessages(messages);
        setShowBirthdayCelebration(true);
        return;
      }

      // Check if user has dateOfBirth and if today is their birthday
      const userDOB = userInfo.dateOfBirth;
      
      if (userDOB) {
        console.log('✅ User has DOB, checking if today is birthday...');
        console.log('📅 DOB value:', userDOB);
        console.log('📅 DOB type:', typeof userDOB);
        
        // Handle different date formats
        let dobToCheck = userDOB;
        if (typeof userDOB === 'object' && userDOB.$date) {
          // MongoDB date object format
          dobToCheck = userDOB.$date;
          console.log('🔄 Converted MongoDB date:', dobToCheck);
        } else if (typeof userDOB === 'string' && userDOB.includes('T')) {
          // ISO date string format
          dobToCheck = userDOB.split('T')[0];
          console.log('🔄 Converted ISO date:', dobToCheck);
        }
        
        const isBirthday = isTodayUserBirthday(dobToCheck);
        console.log('🎂 Is today user\'s birthday?', isBirthday);
        
        if (isBirthday) {
          // Check if birthday was already celebrated today
          const alreadyCelebrated = wasBirthdayCelebratedToday(userInfo._id);
          console.log('🎊 Already celebrated today?', alreadyCelebrated);
          
          if (!alreadyCelebrated) {
            console.log('🎉 Today is user\'s birthday! Starting celebration...');
            
            // Calculate age and prepare messages
            const age = calculateAge(dobToCheck) || 25; // Default age if calculation fails
            const messages = getBirthdayMessages(userInfo.name || 'User', age);
            setBirthdayMessages(messages);
            
            // Show birthday notification
            showBirthdayNotification(userInfo.name || 'User');
            
            // Start birthday celebration
            setShowBirthdayCelebration(true);
            
            // Mark as celebrated today
            markBirthdayCelebrated(userInfo._id, new Date().toDateString());
          } else {
            console.log('🎂 Birthday already celebrated today');
          }
        } else {
          console.log('📅 Today is not user\'s birthday');
        }
      } else {
        console.log('❌ User does not have dateOfBirth field');
        console.log('💡 Tip: Add DOB to user profile to enable birthday celebration');
        
        // Optional: Show a subtle reminder to add DOB (only once per session)
        const dobReminderShown = sessionStorage.getItem('dob_reminder_shown');
        if (!dobReminderShown && userInfo.role === 'Admin') {
          console.log('💡 Showing DOB reminder for admin user');
          sessionStorage.setItem('dob_reminder_shown', 'true');
          // Could show a toast notification here if needed
        }
      }
    };

    checkBirthday();
  }, [userInfo]);

  const handleCelebrationComplete = () => {
    console.log('🎊 Birthday celebration completed');
    setShowBirthdayCelebration(false);
  };

  // If birthday celebration is active, show it instead of dashboard
  if (showBirthdayCelebration) {
    return (
      <BirthdayCelebration
        userName={userInfo?.name || 'User'}
        messages={birthdayMessages}
        onComplete={handleCelebrationComplete}
      />
    );
  }

  // Otherwise, render the wrapped component (dashboard)
  return children;
};

export default BirthdayWrapper;
